import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import { useAuth } from '../context/AuthContext';

const Messages = () => {
  const [searchParams] = useSearchParams();
  const recipientId = searchParams.get('recipient');
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const recipientProcessedRef = useRef(false);
  const { user, token } = useAuth();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    // Only initialize if user is available
    if (!user || !user._id) {
      setLoading(false);
      return;
    }

    // Initialize socket connection
    const newSocket = io(API_URL);
    socketRef.current = newSocket;
    setSocket(newSocket);

    // Join with user ID
    newSocket.emit('join', user._id);

    fetchConversations();

    return () => {
      newSocket.close();
      socketRef.current = null;
    };
  }, [user]);

  // Listen for incoming messages with proper cleanup
  useEffect(() => {
    const currentSocket = socketRef.current;
    if (!currentSocket) return;

    const handleNewMessage = (message) => {
      if (selectedConversation && 
          (message.sender._id === selectedConversation._id || 
           message.recipient._id === selectedConversation._id)) {
        setMessages(prev => [...prev, message]);
      }
    };

    currentSocket.on('newMessage', handleNewMessage);
    return () => currentSocket.off('newMessage', handleNewMessage);
  }, [selectedConversation]);

  // Handle recipient from URL params (one-time)
  useEffect(() => {
    if (!recipientId || !user || recipientProcessedRef.current) return;
    
    // Check if conversations are loaded
    if (!loading && conversations.length >= 0) {
      recipientProcessedRef.current = true;
      const conversation = conversations.find(c => c.user._id === recipientId);
      if (conversation) {
        handleSelectConversation(conversation.user);
      } else {
        fetchUserAndStartConversation(recipientId);
      }
    }
  }, [recipientId, conversations, user, loading]);

  const fetchUserAndStartConversation = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/artist/${userId}`);
      const artist = response.data.artist;
      setSelectedConversation(artist);
      setMessages([]);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConversation = async (recipient) => {
    setSelectedConversation(recipient);
    try {
      const response = await axios.get(
        `${API_URL}/api/messages/conversation/${recipient._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data);

      // Mark as read
      await axios.put(
        `${API_URL}/api/messages/read`,
        { senderId: recipient._id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || !user) return;

    const messageData = {
      sender: user._id,
      recipient: selectedConversation._id,
      content: newMessage.trim()
    };

    // Emit via socket
    if (socket) {
      socket.emit('sendMessage', messageData);
    }

    // Add to local state
    setMessages(prev => [...prev, {
      ...messageData,
      sender: { _id: user._id, name: user.name },
      recipient: selectedConversation,
      createdAt: new Date()
    }]);

    setNewMessage('');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Please login to access messages</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-red-50 flex flex-col">
      <div className="flex-1 max-w-7xl mx-auto w-full px-2 sm:px-4 lg:px-8 py-4 sm:py-6">
        <div className="bg-white rounded-3xl shadow-african-lg h-full flex flex-col md:flex-row overflow-hidden">
          {/* Conversations List */}
          <div className={`w-full md:w-1/3 border-r border-orange-100 flex flex-col ${
            selectedConversation ? 'hidden md:flex' : 'flex'
          }`}>
            {/* Header */}
            <div className="p-4 sm:p-6 border-b border-orange-100">
              <div className="flex items-center justify-between">
                {/* Back button on mobile */}
                {selectedConversation && (
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="md:hidden mr-3 p-2 hover:bg-orange-50 rounded-full"
                  >
                    <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <h2 className="text-xl sm:text-2xl font-display font-bold text-gray-900">
                  Messages
                </h2>
              </div>
            </div>

            {/* Conversations */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {conversations.length > 0 ? (
                conversations.map((conversation) => (
                  <button
                    key={conversation.user._id}
                    onClick={() => handleSelectConversation(conversation.user)}
                    className={`w-full p-4 flex items-center space-x-3 hover:bg-orange-50 transition-colors duration-200 border-b border-orange-50 ${
                      selectedConversation?._id === conversation.user._id ? 'bg-orange-50' : ''
                    }`}
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                      {conversation.user.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {conversation.user.name}
                      </h3>
                      <p className="text-sm text-gray-600 truncate">
                        {conversation.lastMessage?.content}
                      </p>
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatTime(conversation.lastMessage?.createdAt)}
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p>No conversations yet</p>
                  <p className="text-sm mt-2">Start chatting with artists!</p>
                </div>
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className={`flex-1 flex flex-col ${
            selectedConversation ? 'flex' : 'hidden md:flex'
          }`}>
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-4 sm:p-6 border-b border-orange-100 flex items-center space-x-4">
                  {/* Back button on mobile */}
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="md:hidden p-2 hover:bg-orange-50 rounded-full"
                  >
                    <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                    {selectedConversation.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-gray-900 truncate">{selectedConversation.name}</h3>
                    <p className="text-xs sm:text-sm text-green-600 flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Active now
                    </p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 custom-scrollbar">
                  {messages.map((message, index) => {
                    const isOwn = message.sender._id === user._id;
                    return (
                      <div
                        key={index}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-xs lg:max-w-md ${isOwn ? 'order-2' : 'order-1'}`}>
                          <div
                            className={`px-4 py-3 rounded-2xl ${
                              isOwn
                                ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-br-none'
                                : 'bg-gray-100 text-gray-900 rounded-bl-none'
                            }`}
                          >
                            <p className="break-words">{message.content}</p>
                          </div>
                          <p className={`text-xs text-gray-500 mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                            {formatTime(message.createdAt)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-4 sm:p-6 border-t border-orange-100">
                  <div className="flex space-x-2 sm:space-x-4">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      className="flex-1 px-3 sm:px-4 py-2 sm:py-3 rounded-full border-2 border-orange-200 focus:border-orange-500 focus:ring-4 focus:ring-orange-100 outline-none transition-all duration-200 text-sm sm:text-base"
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim()}
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-full font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <svg className="w-24 h-24 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p className="text-xl font-semibold">Select a conversation</p>
                  <p className="text-sm mt-2">Choose a conversation to start messaging</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;